import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { State } from 'src/app/store';
import { environmentSelector } from 'src/app/store/global/global.selectors';
import { CollectsAiAnalyticsBase, UsageAnalytics, SessionAnalytics, PerformanceAnalytics, FeedbackAnalytics } from './analytics.service';

@Injectable({ providedIn: 'root' })
export class CollectsAiAnalyticsService implements CollectsAiAnalyticsBase {
    baseUrl = {
        DEV: 'https://bciti-vm.canadaeast.cloudapp.azure.com/dev/agent/collects-agent',
        UAT: 'https://bciti-vm.canadaeast.cloudapp.azure.com/uat/agent/collects-agent',
        PROD: 'https://bciti-vm.canadaeast.cloudapp.azure.com/prod/agent/collects-agent',
    };

    constructor(private http: HttpClient, private store: Store<State>) {}

    getUsageAnalytics(orgTag: string, startDate?: string, endDate?: string): Observable<UsageAnalytics> {
        return this.store.select(environmentSelector).pipe(
            take(1),
            switchMap((env) => {
                let params = new HttpParams();
                if (startDate) {
                    params = params.set('start_date', startDate);
                }
                if (endDate) {
                    params = params.set('end_date', endDate);
                }

                return this.http
                    .get(`${this.baseUrl[env.label]}/api/v1/analytics/usage`, {
                        params,
                        headers: { 'x-organisation': orgTag },
                    })
                    .pipe(map((res) => res as UsageAnalytics));
            }),
        );
    }

    getSessionAnalytics(orgTag: string, startDate?: string, endDate?: string): Observable<SessionAnalytics> {
        return this.store.select(environmentSelector).pipe(
            take(1),
            switchMap((env) => {
                let params = new HttpParams();
                if (startDate) {
                    params = params.set('start_date', startDate);
                }
                if (endDate) {
                    params = params.set('end_date', endDate);
                }

                return this.http
                    .get(`${this.baseUrl[env.label]}/api/v1/analytics/sessions`, {
                        params,
                        headers: { 'x-organisation': orgTag },
                    })
                    .pipe(map((res) => res as SessionAnalytics));
            }),
        );
    }

    getPerformanceAnalytics(orgTag: string, startDate?: string, endDate?: string): Observable<PerformanceAnalytics> {
        return this.store.select(environmentSelector).pipe(
            take(1),
            switchMap((env) => {
                let params = new HttpParams();
                if (startDate) {
                    params = params.set('start_date', startDate);
                }
                if (endDate) {
                    params = params.set('end_date', endDate);
                }

                return this.http
                    .get(`${this.baseUrl[env.label]}/api/v1/analytics/performance`, {
                        params,
                        headers: { 'x-organisation': orgTag },
                    })
                    .pipe(map((res) => res as PerformanceAnalytics));
            }),
        );
    }

    getFeedbackAnalytics(orgTag: string, startDate?: string, endDate?: string): Observable<FeedbackAnalytics> {
        return this.store.select(environmentSelector).pipe(
            take(1),
            switchMap((env) => {
                let params = new HttpParams();
                if (startDate) {
                    params = params.set('start_date', startDate);
                }
                if (endDate) {
                    params = params.set('end_date', endDate);
                }

                return this.http
                    .get(`${this.baseUrl[env.label]}/api/v1/analytics/feedback`, {
                        params,
                        headers: { 'x-organisation': orgTag },
                    })
                    .pipe(map((res) => res as FeedbackAnalytics));
            }),
        );
    }
}

export const ANALYTICS_SERVICE_PROVIDER = {
    provide: CollectsAiAnalyticsBase,
    useClass: CollectsAiAnalyticsService,
};
